import { useId, useState } from "react"

import { Input } from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
}

/**
 * Info-tooltip copy for outbound voice agent arguments. Mirrors
 * INBOUND_VOICE_TOOLTIPS so both voice agent forms read consistently.
 */
const OUTBOUND_VOICE_TOOLTIPS = {
  triggerCallEndpoint:
    "The HTTPS webhook Coval calls to kick off an outbound call with your agent.",
  triggerCallHeaders:
    "Optional HTTP headers (JSON) sent with the trigger-call request, e.g. an Authorization bearer token.",
  phoneNumberKey:
    'The payload key Coval uses to pass the phone number to your endpoint. Defaults to "phone_number".',
  triggerCallPayload:
    "Optional JSON body sent with the trigger-call request, e.g. a sequence code or campaign id.",
} as const

interface TriggerCallConfig {
  trigger_call_endpoint: string
  trigger_call_headers: string
  trigger_call_payload: string
  phone_number_key?: string
}

interface OutboundCallConfigFieldsProps extends BaseFormFieldProps {
  config: TriggerCallConfig
  onConfigChange: (config: TriggerCallConfig) => void
}

export const OutboundCallConfigFields: React.FC<
  OutboundCallConfigFieldsProps
> = ({ config, onConfigChange, disabled, className = "" }) => {
  const [headersError, setHeadersError] = useState<string | null>(null)
  const [payloadError, setPayloadError] = useState<string | null>(null)

  const endpointLabelId = useId()
  const endpointInputId = useId()
  const endpointDescId = useId()
  const headersLabelId = useId()
  const headersDescId = useId()
  const phoneKeyLabelId = useId()
  const phoneKeyInputId = useId()
  const phoneKeyDescId = useId()
  const payloadLabelId = useId()
  const payloadDescId = useId()

  const handleEndpointChange = (value: string) => {
    onConfigChange({
      ...config,
      trigger_call_endpoint: value,
    })
  }

  const handleHeadersChange = (value: string) => {
    if (value.trim() === "") {
      setHeadersError(null)
      onConfigChange({ ...config, trigger_call_headers: value })
      return
    }
    try {
      JSON.parse(value)
      setHeadersError(null)
    } catch {
      setHeadersError("Invalid JSON format")
    }
    onConfigChange({ ...config, trigger_call_headers: value })
  }

  const handlePayloadChange = (value: string) => {
    if (value.trim() === "") {
      setPayloadError(null)
      onConfigChange({ ...config, trigger_call_payload: value })
      return
    }
    try {
      JSON.parse(value)
      setPayloadError(null)
    } catch {
      setPayloadError("Invalid JSON format")
    }
    onConfigChange({ ...config, trigger_call_payload: value })
  }

  const handlePhoneNumberKeyChange = (value: string) => {
    onConfigChange({ ...config, phone_number_key: value })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <FieldLabel
          id={endpointLabelId}
          htmlFor={endpointInputId}
          required
          tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallEndpoint}
        >
          Trigger Call Endpoint
        </FieldLabel>
        <Input
          id={endpointInputId}
          placeholder="https://your-endpoint.com/webhook"
          value={config.trigger_call_endpoint}
          onChange={(e) => handleEndpointChange(e.target.value)}
          className={className}
          disabled={disabled}
          aria-describedby={endpointDescId}
        />
        <p id={endpointDescId} className="text-sm text-coval-text-muted">
          The endpoint URL where outbound calls will be triggered. This should
          be a valid HTTPS URL that can handle webhook requests from the voice
          system.
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel
          id={headersLabelId}
          tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallHeaders}
        >
          Trigger Call Headers
        </FieldLabel>
        <JsonCodeEditor
          placeholder={'{\n  "Authorization": "Bearer token"\n}'}
          value={config.trigger_call_headers}
          onChange={handleHeadersChange}
          className={className}
          disabled={disabled}
          rows={3}
          error={headersError}
          ariaLabelledBy={headersLabelId}
          ariaDescribedBy={headersDescId}
        />
        <p
          id={headersDescId}
          className="text-sm text-coval-text-muted font-mono"
        >
          {`HTTP headers to include in the trigger call request. Must be valid JSON format. Example: {"Content-Type": "application/json", "Authorization": "Bearer token"}`}
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel
          id={phoneKeyLabelId}
          htmlFor={phoneKeyInputId}
          tooltip={OUTBOUND_VOICE_TOOLTIPS.phoneNumberKey}
        >
          Phone Number Key
        </FieldLabel>
        <Input
          id={phoneKeyInputId}
          placeholder="phone_number"
          value={config.phone_number_key || ""}
          onChange={(e) => handlePhoneNumberKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          aria-describedby={phoneKeyDescId}
        />
        <p id={phoneKeyDescId} className="text-sm text-coval-text-muted">
          The key used to identify the phone number in the trigger call payload.
          Coval will use this key to add the phone number when sending the
          trigger call request. If not specified, defaults to
          &ldquo;phone_number&rdquo;.
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel
          id={payloadLabelId}
          tooltip={OUTBOUND_VOICE_TOOLTIPS.triggerCallPayload}
        >
          Trigger Call Payload
        </FieldLabel>
        <JsonCodeEditor
          placeholder={'{\n  "sequence_code": "ABC123"\n}'}
          value={config.trigger_call_payload}
          onChange={handlePayloadChange}
          className={className}
          disabled={disabled}
          rows={3}
          error={payloadError}
          ariaLabelledBy={payloadLabelId}
          ariaDescribedBy={payloadDescId}
        />
        <p
          id={payloadDescId}
          className="text-sm text-coval-text-muted font-mono"
        >
          {`JSON payload to send with the trigger call request. Must be valid JSON format. Example: {"sequence_code": "ABC123"}`}
        </p>
      </div>
    </div>
  )
}
